import { ChartRangeType } from "components";
import { getEndOfDateByTimeUnit, getStartOfDateByTimeUnit } from "helpers";
import moment from "moment";
import { useMemo, useState } from "react";

import { RevenueBreakdownParams } from "../../apis";
import { FinancialBucket, FinancialComparison } from "../../constants";

const PRESET_UNIT: Partial<Record<ChartRangeType, "week" | "month" | "year">> =
  {
    [ChartRangeType.Week]: "week",
    [ChartRangeType.Month]: "month",
    [ChartRangeType.Year]: "year"
  };

const formatDate = (date: Date) => moment(date).format("YYYY-MM-DD");

const defaultRange = (): [Date, Date] => [
  getStartOfDateByTimeUnit(new Date(), "week"),
  getEndOfDateByTimeUnit(new Date(), "week")
];

const useFinancialPeriod = () => {
  const [preset, setPreset] = useState<ChartRangeType>(ChartRangeType.Week);
  const [customRange, setCustomRange] = useState<[Date, Date]>(defaultRange());
  const [comparison, setComparison] = useState<FinancialComparison>(
    FinancialComparison.None
  );
  const [customComparisonRange, setCustomComparisonRange] =
    useState<[Date, Date]>(defaultRange());

  const range = useMemo<[Date, Date]>(() => {
    if (preset === ChartRangeType.Custom) return customRange;

    const unit = PRESET_UNIT[preset] ?? "week";
    const now = new Date();
    return [
      getStartOfDateByTimeUnit(now, unit),
      getEndOfDateByTimeUnit(now, unit)
    ];
  }, [preset, customRange]);

  const bucket =
    preset === ChartRangeType.Year
      ? FinancialBucket.Month
      : FinancialBucket.Day;

  const periodParams = useMemo(
    () => ({ from: formatDate(range[0]), to: formatDate(range[1]) }),
    [range]
  );

  const revenueBreakdownParams = useMemo<RevenueBreakdownParams>(() => {
    const params: RevenueBreakdownParams = {
      ...periodParams,
      bucket,
      comparison
    };

    if (comparison === FinancialComparison.Custom) {
      params.comparisonFrom = formatDate(customComparisonRange[0]);
      params.comparisonTo = formatDate(customComparisonRange[1]);
    }

    return params;
  }, [periodParams, bucket, comparison, customComparisonRange]);

  return {
    preset,
    setPreset,
    range,
    customRange,
    setCustomRange,
    comparison,
    setComparison,
    customComparisonRange,
    setCustomComparisonRange,
    periodParams,
    revenueBreakdownParams
  };
};

export default useFinancialPeriod;
